import React, { useContext, useMemo, useState } from "react";
import * as yup from "yup";
import clsx from "clsx";
import { Formik, FormikHelpers } from "formik";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
  HvGrid,
  HvTooltip,
  HvTypography,
} from "@hitachivantara/uikit-react-core";

import { useSnackbar } from "lib/provider/SnackbarProvider";
import usePreventNavigation from "lib/hooks/usePreventNavigation";
import { addUser, useTimeSettings } from "lib/api";
import Container from "components/layout/Container";
import Loading from "components/layout/Loading";
import Banner from "components/common/Banner";
import { FormHeader } from "components/form";
import moment from "moment";
import { Settings } from "@hitachivantara/uikit-react-icons";
import Details from "components/membersSettings/AddEdit/Details/Details";
import {
  getDateTimeInUTCByZoneWithoutFormat,
  standardDateFormat,
} from "lib/utils/datetime";
import { ModuleNames, ModulePolicyTypes } from "lib/api/userSettings";
import { UserDataContext } from "lib/context/UserDataContext";
import NotFound from "components/common/NotFound";
import useStyles from "./styles";

const Add: React.FC = () => {
  const { t } = useTranslation("userAddEdit");
  const history = useHistory();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [, setPreventNavigation] = usePreventNavigation();
  const [showBanner, setShowBanner] = useState(false);
  const [errorBannerCode, setBannerErrorCode] = useState("");
  const { timezone } = useTimeSettings();
  const sections = [{ label: t("details.title"), value: "details" }];

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        username: yup
          .string()
          .trim()
          .required(t("validation.required"))
          .matches(/^[^<>(),:;@[\]]*$/, t("validation.userInvalidChars")),

        password: yup.string().required(t("validation.required")),
        confirmation_password: yup
          .string()
          .required(t("validation.required"))
          .oneOf([yup.ref("password"), null], t("validation.passwordMatch")),
        email: yup.string().required(t("validation.required")),
        expiration_date: yup.date().required(t("validation.required")),
        expiration_time: yup.object().shape({
          hours: yup.number().required(t("validation.required")),
          minutes: yup.number().required(t("validation.required")),
          seconds: yup.number().required(t("validation.required")),
        }),
      }),
    [t]
  );

  const onSave = (data: UserDetailsFormData) => {
    const date = moment(data.expiration_date).format("YYYY-MM-DD");
    // eslint-disable-next-line camelcase
    const final_date = moment(
      `${date} ${data.expiration_time.hours}:${data.expiration_time.minutes}:${data.expiration_time.seconds}`
    ).format(standardDateFormat);
    const formattedFinalDate = moment(
      getDateTimeInUTCByZoneWithoutFormat(final_date, timezone)
    ).format(standardDateFormat);
    return addUser({
      email: data.email,
      is_integration: true,
      name: data.username,
      password: data.password,
      password_exp_timestamp: formattedFinalDate,
      surname: "",
    });
  };

  const onSubmitHandler = async (
    values: UserDetailsFormData,
    actions: FormikHelpers<UserDetailsFormData>
  ) => {
    try {
      setBannerErrorCode("");
      await onSave(values);
      enqueueSnackbar(t("validation.success.add"), { variant: "success" });
      setPreventNavigation(false);
      history.push("/settings/users");
      actions.setStatus({});
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const errorCode = error?.response?.data?.error?.code;

      if (errorCode?.endsWith("user_already_exists")) {
        actions.setStatus({
          serverSideErrors: {
            username: t(`validation.unknownError.user_already_exists`),
          },
        });
        return;
      }
      if (errorCode?.endsWith("password_invalid_constraints")) {
        actions.setStatus({
          serverSideErrors: {
            password: t(`validation.unknownError.password_invalid_constraints`),
          },
        });
        return;
      }

      setBannerErrorCode(errorCode);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const errorStatus = error?.response?.status;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const jsonError = error.toJSON();
      if (errorStatus === 405) setBannerErrorCode("service_not_available_add");
      if (jsonError.message === "Network Error")
        setBannerErrorCode("service_not_available_add");

      setShowBanner(true);
    }
  };

  const initialValues: UserDetailsFormData = {
    username: "",
    password: "",
    confirmation_password: "",
    old_password: "",
    email: "",
    expiration_date: undefined,
    expiration_time: undefined,
  };

  return (
    <Formik
      initialStatus={{ serverSideErrors: {} }}
      initialValues={initialValues as UserDetailsFormData}
      validationSchema={validationSchema}
      onSubmit={onSubmitHandler}
    >
      {(props) => {
        const { handleSubmit, isSubmitting } = props;

        const goBackHandler = () => {
          history.goBack();
        };

        const onBannerCloseHandler = () => {
          setShowBanner(false);
          setBannerErrorCode("");
        };

        return (
          <Container className={classes.container} maxWidth="xl">
            {isSubmitting && (
              <Loading
                delay={500}
                loadingLabel={t("submiting")}
                className={clsx(classes.loading, classes.zIndex)}
              />
            )}
            <Banner
              open={showBanner}
              variant="error"
              showIcon
              label={t(
                errorBannerCode
                  ? `validation.unknownError.${errorBannerCode}`
                  : `validation.unknownError.add`
              )}
              onClose={onBannerCloseHandler}
            />
            <form onSubmit={handleSubmit} noValidate>
              <FormHeader
                title={t("title")}
                icon={
                  <HvTooltip
                    style={{
                      position: "absolute",
                    }}
                    title={
                      <HvTypography>{t("tooltip.serviceAccount")}</HvTypography>
                    }
                  >
                    <div>
                      <Settings className={classes.icon} />
                    </div>
                  </HvTooltip>
                }
                onCancel={goBackHandler}
              />
              <HvGrid container spacing={0}>
                <HvGrid xs={2} className={classes.firstLastColumn} />
                <HvGrid
                  container
                  spacing={0}
                  tabIndex={-1}
                  id={sections[0].value}
                  className={classes.form}
                  xs={8}
                  item
                >
                  <HvGrid
                    className={classes.sectionMargin}
                    tabIndex={-1}
                    id={sections[0].value}
                    xs={12}
                    item
                  >
                    <Details isIntegration />
                  </HvGrid>
                </HvGrid>
                <HvGrid xs={2} className={classes.firstLastColumn} />
              </HvGrid>
            </form>
          </Container>
        );
      }}
    </Formik>
  );
};
export const UserAddAuth: React.FC = () => {
  const { hasModulePolicy } = useContext(UserDataContext);
  const hasRight = useMemo(() => {
    return hasModulePolicy(ModuleNames.core, ModulePolicyTypes.usergroupsAdmin);
  }, [hasModulePolicy]);
  return (
    <>
      {hasRight ? (
        <Container noHVContainer>
          <Add />
        </Container>
      ) : (
        <NotFound />
      )}
    </>
  );
};
export default Add;
